import type { JSX } from "preact";
import { useEffect, useState } from "preact/hooks";
import type {
    MeasurementStat,
    MeasurementStatDimension,
    MeasurementStatQuestion,
} from "@gss/types/measurementStat";
import type { SecurityContext } from "@gss/types/SecurityContext";
import type {
    DimensionScore,
    QuestionScore,
    QuestionScoreQuestion,
} from "@gss/types/statScore";
import type { LanguageTranslation } from "../../translations/translation.types";
import { Chart } from "../charts/Chart";
import { DAO } from "../DAO";
import type {
    FilterSerie,
    FilterSeriesDimensionScoreDimension,
    FilterSeriesStat,
    QuestionsData,
    QuestionsDataList,
} from "../filters/filters.types";
import { TooFewRespondents } from "../util/TooFewRespondents";
import { useTranslation } from "../util/Translation";
import { Question } from "./Question";
import {
    getOverviewCategories,
    getOverviewData,
} from "./QuestionOverviewTotal";

interface Props {
    stat: MeasurementStat;
    dimension: MeasurementStatDimension;
    questions: MeasurementStatQuestion[];
    subtitle: string;
    filterSeries: FilterSerie[];
}

export function Dimension({
    stat,
    dimension,
    questions,
    subtitle,
    filterSeries,
}: Props): JSX.Element {
    const [data, setData] = useState<{
        scoreOk: FilterSeriesDimensionScoreDimension[];
        scoresNotOk: FilterSeriesStat[];
        questionsMap: Record<string, QuestionsData>;
        questionsList: QuestionsDataList[];
    }>();
    const { translation, getTranslation } = useTranslation();
    const t = translation.stats;

    useEffect(() => {
        void getData(stat.securityContext, filterSeries).then(
            ([dimensionScores, questionScores]) => {
                const scoresData = dimension.hasScores
                    ? getScoreData(filterSeries, dimensionScores, dimension)
                    : { ok: [], notOk: [] };

                const questionData = getQuestionData(
                    filterSeries,
                    dimensionScores,
                    questionScores,
                    questions,
                );

                setData({
                    scoreOk: scoresData.ok,
                    scoresNotOk: scoresData.notOk,
                    questionsMap: questionData.map,
                    questionsList: questionData.list,
                });
            },
        );
    }, [filterSeries]);

    const renderDimScores = () => {
        if (!dimension.hasScores) {
            return null;
        }
        const title = t.dimensionScore;
        return (
            <>
                <h3>{title}</h3>
                <p>{t.dimensionScoreDesc}</p>
                <TooFewRespondents series={data?.scoresNotOk} />
                <Chart
                    variant="column"
                    title={dimension.name}
                    subtitle={subtitle}
                    filename={`${stat.measurement} ${dimension.name} ${title}`}
                    yTitle={t.dimensionScoreYAxis}
                    series={data?.scoreOk}
                    categories={undefined}
                    callback={(s) => ({
                        data: [s.stat.score],
                        confidenceInterval: s.stat.confidenceInterval,
                    })}
                />
                <hr />
            </>
        );
    };

    const renderQuestionOverview = () => {
        if (!dimension.hasScores || questions.length < 2) {
            return null;
        }
        const title = t.questionOverview;
        return (
            <>
                <h3>{title}</h3>
                <Chart
                    variant="column"
                    title={dimension.name}
                    subtitle={subtitle}
                    yTitle={t.meanValueYAxis}
                    filename={`${stat.measurement} ${dimension.name} ${title}`}
                    categories={getOverviewCategories(
                        getTranslation,
                        questions,
                    )}
                    series={data?.questionsList}
                    callback={(s) =>
                        getOverviewData(getTranslation, questions, s.questions)
                    }
                />
                <hr />
            </>
        );
    };

    const renderQuestions = () => (
        <>
            <h3>{t.questions}</h3>
            {questions.map((q, i) => (
                <>
                    {i > 0 && <hr className="hr-light" />}
                    <Question
                        key={q.id}
                        measurement={stat.measurement}
                        question={q}
                        subtitle={subtitle}
                        series={data?.questionsMap[q.id]}
                    />
                </>
            ))}
        </>
    );

    return (
        <>
            {renderDescription(t, dimension)}
            {renderDimScores()}
            {renderQuestionOverview()}
            {renderQuestions()}
        </>
    );
}

export function filterDimensionQuestions(
    questions: MeasurementStatQuestion[],
    dimension: MeasurementStatDimension,
): MeasurementStatQuestion[] {
    return questions.filter(
        (q) => !q.choiceFreeText && q.dimension === dimension.name,
    );
}

function renderDescription(
    t: LanguageTranslation["stats"],
    dimension: MeasurementStatDimension,
): JSX.Element | null {
    if (dimension.description == null) {
        return null;
    }
    return (
        <div className="mb-3">
            <em>
                {dimension.description}
                &nbsp;
                <a
                    href="https://patientenkat.se/nationellpatientenkat/omnationellpatientenkat/modellforpatientupplevelser/dimensionerochviktning.44360.html"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {t.dimensionsLink}
                </a>
            </em>
        </div>
    );
}

function getData(
    audience: SecurityContext,
    filterSeries: FilterSerie[],
): Promise<[(DimensionScore | undefined)[], (QuestionScore | undefined)[]]> {
    const dimensionPromises = Promise.all(
        filterSeries.map((fs) => {
            return DAO.stats
                .getDimensionScore(
                    audience,
                    fs.measurement,
                    fs.orgStructure,
                    fs.periods,
                    fs.filters,
                    fs.orgIds,
                )
                .catch(() => undefined);
        }),
    );

    const questionPromises = Promise.all(
        filterSeries.map((fs) => {
            return DAO.stats
                .getQuestionScore(
                    audience,
                    fs.measurement,
                    fs.orgStructure,
                    fs.periods,
                    fs.filters,
                    fs.orgIds,
                )
                .catch(() => undefined);
        }),
    );

    return Promise.all([dimensionPromises, questionPromises]);
}

function getScoreData(
    filterSeries: FilterSerie[],
    dimensionScores: (DimensionScore | undefined)[],
    dimension: MeasurementStatDimension,
): { ok: FilterSeriesDimensionScoreDimension[]; notOk: FilterSeriesStat[] } {
    const ok: FilterSeriesDimensionScoreDimension[] = [];
    const notOk: FilterSeriesStat[] = [];

    for (const [i, filterSerie] of filterSeries.entries()) {
        const score = dimensionScores[i];
        if (score?.status === "ok") {
            const dim = score.dimensions.find((d) => d.name === dimension.name);
            if (dim != null) {
                ok.push({ filterSerie, stat: dim });
            } else {
                notOk.push({ filterSerie, stat: score });
            }
        } else {
            notOk.push({ filterSerie });
        }
    }

    return { ok, notOk };
}

function getQuestionData(
    filterSeries: FilterSerie[],
    dimensionScores: (DimensionScore | undefined)[],
    questionScores: (QuestionScore | undefined)[],
    questions: MeasurementStatQuestion[],
): { list: QuestionsDataList[]; map: Record<string, QuestionsData> } {
    const list: QuestionsDataList[] = [];
    const map: Record<string, QuestionsData> = {};

    for (const q of questions) {
        map[q.id] = { ok: [], notOk: [] };
    }

    for (const [i, filterSerie] of filterSeries.entries()) {
        const dimScore = dimensionScores[i];
        const qScore = questionScores[i];
        const questionList: (QuestionScoreQuestion | null)[] = [];
        for (const q of questions) {
            if (qScore != null) {
                const question = qScore.questions.find((cq) => q.id === cq.id);
                questionList.push(question ?? null);
                if (question == null) {
                    map[q.id].notOk.push({ filterSerie });
                } else if (question.status === "ok") {
                    const numRespondents = dimScore?.invitees.answered ?? 0;
                    map[q.id].ok.push({
                        filterSerie,
                        question,
                        numRespondents,
                    });
                } else {
                    map[q.id].notOk.push({ filterSerie, question });
                }
            } else {
                map[q.id].notOk.push({ filterSerie });
            }
        }

        list.push({ filterSerie, questions: questionList });
    }

    return { list, map };
}
